import React from 'react';
import clsx from 'clsx';

import {Typography, capitalize} from '@mui/material';
import {
	SEVERITIES,
	Severity,
	composeClasses,
	styled,
	WithClasses,
} from '@netx/core-components/Theme';

import {
	FontAwesomeIcon,
	type FontAwesomeIconProps,
} from '@fortawesome/react-fontawesome';
import type {IconDefinition} from '@fortawesome/fontawesome-svg-core';

import {
	fallbackMessageClasses,
	FallbackMessageClasses,
	getFallbackMessageUtilityClass,
	FALLBACK_MESSAGE_CLASS_NAME,
} from './fallbackMessageClasses';

type FallbackMessageUtiltiyClassProps = WithClasses<
	FallbackMessageClasses,
	{
		severity?: Severity;
		variant?: 'default' | 'light';
	}
>;

const useUtilityClasses = (ownerState: FallbackMessageUtiltiyClassProps) => {
	const {classes, severity, variant} = ownerState;
	const slots = {
		root: ['root', variant, severity && `severity${capitalize(severity)}`],
		icon: ['icon', variant, severity && `severity${capitalize(severity)}`],
		text: ['text', variant, severity && `severity${capitalize(severity)}`],
		children: ['children'],
		body: ['body'],
		header: ['header', variant],
	};
	return composeClasses(slots, getFallbackMessageUtilityClass, classes);
};

const FallbackMessageRoot = styled('div', {
	name: FALLBACK_MESSAGE_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	alignItems: 'center',
	background: theme.palette.background.paper,
	flex: '1 1 auto',
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	minHeight: 0,
	overflow: 'hidden',
	padding: theme.spacing(3),
	textAlign: 'center',
	width: '100%',
}));

const FallbackMessageIcon = styled(FontAwesomeIcon, {
	name: FALLBACK_MESSAGE_CLASS_NAME,
	slot: 'Icon',
})(({theme}) => ({
	color: theme.palette.custom.icon.onBackground,
	height: '75px !important',
	width: '75px !important',
	marginBottom: theme.spacing(2),
	[`&.${fallbackMessageClasses.light}`]: {
		...SEVERITIES.reduce((accum, key) => {
			return key === 'default'
				? accum
				: {
						...accum,
						[`&.${
							fallbackMessageClasses[
								`severity${capitalize(
									key,
								)}` as keyof typeof fallbackMessageClasses
							]
						}`]: {
							color: theme.palette[key].light,
						},
				  };
		}, {}),
	},
	[`&:not(.${fallbackMessageClasses.light})`]: {
		...SEVERITIES.reduce((accum, key) => {
			return key === 'default'
				? accum
				: {
						...accum,
						[`&.${
							fallbackMessageClasses[
								`severity${capitalize(
									key,
								)}` as keyof typeof fallbackMessageClasses
							]
						}`]: {
							color: theme.palette[key].main,
						},
				  };
		}, {}),
	},
}));

const FallbackMessageBody = styled('div', {
	name: FALLBACK_MESSAGE_CLASS_NAME,
	slot: 'Body',
})(({theme}) => ({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	color: theme.palette.text.secondary,
	width: '100%',
}));

const FallbackMessageHeader = styled('div', {
	name: FALLBACK_MESSAGE_CLASS_NAME,
	slot: 'Header',
})(({theme}) => ({
	[`&:not(.${fallbackMessageClasses.light}) .${fallbackMessageClasses.text}`]: {
		color: theme.palette.text.primary,
	},
}));

const FallbackMessageText = styled(Typography, {
	name: FALLBACK_MESSAGE_CLASS_NAME,
	slot: 'Text',
})(({theme}) => ({
	[`&.${fallbackMessageClasses.light}`]: {
		color: theme.palette.text.secondary,
	},
}));

const FallbackMessageChildren = styled('div', {
	name: FALLBACK_MESSAGE_CLASS_NAME,
	slot: 'Children',
})(({theme}) => ({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	marginTop: theme.spacing(3),
	width: '100%',
}));

export interface FallbackMessageProps
	extends React.DetailedHTMLProps<
			React.HTMLAttributes<HTMLDivElement>,
			HTMLDivElement
		>,
		React.PropsWithChildren,
		WithClasses<FallbackMessageClasses> {
	icon?: IconDefinition;
	IconProps?: Partial<Omit<FontAwesomeIconProps, 'className' | 'icon'>>;
	title?: string;
	message?: string;
	severity?: Severity;
	variant?: 'default' | 'light';
}

/**
 * A uniformed way to show fallback messages in the app \
 * It will size to whatever container it is put in - it will not influence the size of the parent container
 */
export const FallbackMessage = ({
	classes: _classes,
	className,
	children,
	icon,
	message,
	severity,
	title,
	variant = 'light',
	IconProps,
	...props
}: FallbackMessageProps) => {
	const classes = useUtilityClasses({
		classes: _classes,
		className,
		severity,
		variant,
	});

	return (
		<FallbackMessageRoot {...props} className={clsx(classes.root, className)}>
			<FallbackMessageHeader className={classes.header}>
				{icon && (
					<FallbackMessageIcon
						className={classes.icon}
						icon={icon}
						{...IconProps}
					/>
				)}
				{title ? (
					<FallbackMessageText
						className={classes.text}
						variant="body1"
						gutterBottom
					>
						{title}
					</FallbackMessageText>
				) : null}
			</FallbackMessageHeader>
			<FallbackMessageBody className={classes.body}>
				{message != null && (
					<FallbackMessageText className={classes.text} variant="body2">
						{message}
					</FallbackMessageText>
				)}

				{children ? (
					<FallbackMessageChildren className={classes.children}>
						{children}
					</FallbackMessageChildren>
				) : null}
			</FallbackMessageBody>
		</FallbackMessageRoot>
	);
};

export default FallbackMessage;
